import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PlanFeatureMatrix from "@/components/PlanFeatureMatrix";

const emptyPlan = {
  name: "",
  price_monthly: 99000,
  price_yearly: 990000,
  max_outlets: 1,
  max_users: 3,
  feature_keys: [],
  active: true,
};

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default function Plans() {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState({ categories: [], features: [], dependencies: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPlan);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [plansRes, catalogRes] = await Promise.all([
        api.get("/plans"),
        api.get("/plans/feature-catalog"),
      ]);
      setRows(plansRes.data || []);
      setCatalog(catalogRes.data || { categories: [], features: [], dependencies: {} });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memuat paket");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const featureByKey = useMemo(
    () => Object.fromEntries((catalog.features || []).map((feature) => [feature.key, feature])),
    [catalog.features]
  );

  const expandDependencies = (keys) => {
    const selected = new Set(keys || []);
    let changed = true;
    while (changed) {
      changed = false;
      [...selected].forEach((key) => {
        (catalog.dependencies?.[key] || []).forEach((dependency) => {
          if (!selected.has(dependency)) {
            selected.add(dependency);
            changed = true;
          }
        });
      });
    }
    return (catalog.features || []).map((feature) => feature.key).filter((key) => selected.has(key));
  };

  const openCreate = () => {
    setForm({ ...emptyPlan, feature_keys: [] });
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (plan) => {
    setForm({
      ...emptyPlan,
      ...plan,
      feature_keys: [...(plan.feature_keys || [])],
    });
    setEditId(plan.id);
    setOpen(true);
  };

  const toggleFeature = (key) => {
    setForm((current) => {
      const selected = new Set(current.feature_keys || []);
      if (selected.has(key)) {
        selected.delete(key);
        // Remove dependent features that can no longer operate.
        let changed = true;
        while (changed) {
          changed = false;
          [...selected].forEach((selectedKey) => {
            const needs = catalog.dependencies?.[selectedKey] || [];
            if (needs.some((dependency) => !selected.has(dependency))) {
              selected.delete(selectedKey);
              changed = true;
            }
          });
        }
      } else {
        selected.add(key);
      }
      return { ...current, feature_keys: expandDependencies([...selected]) };
    });
  };

  const toggleCategory = (features) => {
    const keys = features.map((feature) => feature.key);
    const allSelected = keys.every((key) => form.feature_keys.includes(key));
    if (allSelected) {
      setForm((current) => {
        const selected = new Set(current.feature_keys.filter((key) => !keys.includes(key)));
        let changed = true;
        while (changed) {
          changed = false;
          [...selected].forEach((selectedKey) => {
            const needs = catalog.dependencies?.[selectedKey] || [];
            if (needs.some((dependency) => !selected.has(dependency))) {
              selected.delete(selectedKey);
              changed = true;
            }
          });
        }
        return { ...current, feature_keys: [...selected] };
      });
    } else {
      setForm((current) => ({
        ...current,
        feature_keys: expandDependencies([...(current.feature_keys || []), ...keys]),
      }));
    }
  };

  const save = async () => {
    const body = {
      ...form,
      name: form.name.trim(),
      price_monthly: numberValue(form.price_monthly),
      price_yearly: numberValue(form.price_yearly),
      max_outlets: numberValue(form.max_outlets),
      max_users: numberValue(form.max_users),
      feature_keys: expandDependencies(form.feature_keys),
    };
    if (!body.name) return toast.error("Nama paket wajib diisi");
    if (body.max_outlets < 1 || body.max_users < 1) return toast.error("Limit outlet dan pengguna minimal 1");

    setSaving(true);
    try {
      if (editId) await api.patch(`/plans/${editId}`, body);
      else await api.post("/plans", body);
      toast.success("Paket tersimpan");
      setOpen(false);
      setForm(emptyPlan);
      setEditId(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Paket gagal disimpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus paket? Paket yang masih digunakan tidak dapat dihapus.")) return;
    try {
      await api.delete(`/plans/${id}`);
      toast.success("Paket dihapus");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Paket gagal dihapus");
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <div className="overline text-zinc-500">SUBSCRIPTION & ENTITLEMENT</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Paket & Fitur</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Limit outlet, pengguna aktif, dan fitur pada halaman ini diterapkan oleh backend untuk setiap mitra.
          </p>
        </div>
        <button onClick={openCreate} data-testid="plan-add-btn" className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold inline-flex items-center justify-center gap-1">
          <Plus className="h-4 w-4" /> Paket Baru
        </button>
      </div>

      <section className="mb-8">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="overline text-zinc-500">MATRIKS ENTITLEMENT</div>
            <h2 className="font-display font-black text-2xl tracking-tight">Matriks Plan & Fitur</h2>
            <p className="mt-1 text-xs text-zinc-500">Bandingkan harga, kuota, status, dan seluruh fitur setiap plan dalam satu tabel.</p>
          </div>
          <div className="text-xs text-zinc-500 tabular-nums">{rows.length} plan · {(catalog.features || []).length} fitur</div>
        </div>
        <PlanFeatureMatrix plans={rows} features={catalog.features || []} showStatus />
      </section>

      <div className="mb-4">
        <div className="overline text-zinc-500">DAFTAR PLAN</div>
        <h2 className="font-display font-black text-2xl tracking-tight">Kelola detail setiap plan</h2>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((plan) => (
          <div key={plan.id} className={`border bg-white p-6 ${plan.active ? "border-border" : "border-dashed border-zinc-300 opacity-70"}`} data-testid={`plan-row-${plan.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="overline text-[#002FA7]">{plan.active ? "AKTIF" : "NONAKTIF"}</div>
                <h2 className="font-display font-black text-2xl tracking-tight mt-1">{plan.name}</h2>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(plan)} className="p-1.5 hover:bg-zinc-100" data-testid={`plan-edit-${plan.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(plan.id)} className="p-1.5 hover:bg-rose-100 text-rose-600" data-testid={`plan-del-${plan.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="mt-4 font-display font-black text-3xl tabular-nums">{formatIDR(plan.price_monthly)}</div>
            <div className="text-xs text-zinc-500 tabular-nums">{formatIDR(plan.price_yearly)} per tahun</div>

            <div className="grid grid-cols-2 border border-border mt-5">
              <div className="p-3 border-r border-border">
                <div className="overline text-zinc-500">MAX OUTLET</div>
                <div className="font-display font-black text-2xl tabular-nums">{plan.max_outlets}</div>
              </div>
              <div className="p-3">
                <div className="overline text-zinc-500">MAX USER AKTIF</div>
                <div className="font-display font-black text-2xl tabular-nums">{plan.max_users}</div>
              </div>
            </div>

            <div className="mt-5">
              <div className="overline text-zinc-500 mb-2">{(plan.feature_keys || []).length} FITUR</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {(plan.feature_details || []).map((feature) => (
                  <div key={feature.key} className="flex items-start gap-2 text-xs text-zinc-700">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{feature.label}</span>
                  </div>
                ))}
                {(plan.feature_keys || []).length === 0 && <div className="text-xs text-zinc-400">Tidak ada fitur operasional.</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Buat"} Paket</DialogTitle></DialogHeader>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="overline">Nama Paket</label>
              <input data-testid="plan-form-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1" placeholder="Contoh: Starter Pack" />
            </div>
            <div>
              <label className="overline">Harga Bulanan</label>
              <input data-testid="plan-form-monthly" type="number" min="0" value={form.price_monthly} onChange={(event) => setForm({ ...form, price_monthly: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono-num" />
            </div>
            <div>
              <label className="overline">Harga Tahunan</label>
              <input data-testid="plan-form-yearly" type="number" min="0" value={form.price_yearly} onChange={(event) => setForm({ ...form, price_yearly: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono-num" />
            </div>
            <div>
              <label className="overline">Maksimum Outlet Aktif</label>
              <input data-testid="plan-form-outlets" type="number" min="1" value={form.max_outlets} onChange={(event) => setForm({ ...form, max_outlets: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono-num" />
              <div className="text-[11px] text-zinc-500 mt-1">Outlet nonaktif tidak menggunakan kuota.</div>
            </div>
            <div>
              <label className="overline">Maksimum Pengguna Aktif</label>
              <input data-testid="plan-form-users" type="number" min="1" value={form.max_users} onChange={(event) => setForm({ ...form, max_users: event.target.value })} className="w-full border border-border px-3 py-2.5 mt-1 font-mono-num" />
              <div className="text-[11px] text-zinc-500 mt-1">Admin pemilik mitra ikut dihitung sebagai pengguna aktif.</div>
            </div>
            <label className="md:col-span-2 flex items-center justify-between border border-border p-3 cursor-pointer">
              <div>
                <div className="text-sm font-semibold">Paket dapat dipilih mitra</div>
                <div className="text-xs text-zinc-500">Paket nonaktif tetap berlaku untuk pelanggan lama, tetapi tidak muncul pada halaman langganan.</div>
              </div>
              <input type="checkbox" checked={Boolean(form.active)} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
            </label>
          </div>

          <div className="mt-6 border-t border-border pt-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-4">
              <div>
                <div className="overline text-zinc-500">ENTITLEMENT FITUR</div>
                <div className="font-display font-black text-2xl tracking-tight">Pilih fitur paket</div>
                <p className="text-xs text-zinc-500 mt-1">Fitur pendukung yang menjadi dependency akan dipilih otomatis.</p>
              </div>
              <div className="text-sm font-semibold tabular-nums">{form.feature_keys.length} fitur dipilih</div>
            </div>

            <div className="space-y-4">
              {(catalog.categories || []).map((category) => {
                const allSelected = category.features.every((feature) => form.feature_keys.includes(feature.key));
                return (
                  <section key={category.name} className="border border-border">
                    <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-border">
                      <div className="font-semibold text-sm">{category.name}</div>
                      <button type="button" onClick={() => toggleCategory(category.features)} className="text-xs font-semibold text-[#002FA7]">
                        {allSelected ? "Hapus semua" : "Pilih semua"}
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2">
                      {category.features.map((feature, index) => {
                        const selected = form.feature_keys.includes(feature.key);
                        return (
                          <label key={feature.key} className={`p-4 cursor-pointer flex gap-3 ${index % 2 === 0 ? "md:border-r border-border" : ""} border-b border-border last:border-b-0 ${selected ? "bg-blue-50/60" : "bg-white"}`}>
                            <input type="checkbox" checked={selected} onChange={() => toggleFeature(feature.key)} className="mt-1" data-testid={`plan-feature-${feature.key}`} />
                            <div>
                              <div className="text-sm font-semibold">{feature.label}</div>
                              <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{feature.description}</div>
                              {(catalog.dependencies?.[feature.key] || []).length > 0 && (
                                <div className="text-[10px] text-zinc-400 mt-2">
                                  Membutuhkan: {(catalog.dependencies[feature.key] || []).map((key) => featureByKey[key]?.label || key).join(", ")}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 mt-6 sticky bottom-0 bg-white pt-3 border-t border-border">
            <button onClick={() => setOpen(false)} className="flex-1 py-3 border border-border">Batal</button>
            <button onClick={save} disabled={saving} data-testid="plan-save-btn" className="flex-1 py-3 bg-[#002FA7] text-white font-semibold disabled:opacity-50">
              {saving ? "Menyimpan…" : "Simpan Paket"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
